/**
 * Redirect validation utilities
 *
 * Prevents open redirect attacks by validating redirect_uri against
 * a set of trusted origins and an optional trusted domain (for subdomain matching).
 *
 * AUTH-120: Configuration is supplied at runtime via the API-served
 * RuntimeConfig (see lib/runtime-config.ts). The auth UI's own origin
 * (window.location.origin) is always trusted.
 */
import type { RuntimeConfig } from "./runtime-config";

const AUTH_FLOW_PATHS = new Set([
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/2fa",
  "/step-up",
  "/enroll-mfa",
  "/logout",
]);

function getTrustedOrigins(config: RuntimeConfig): Set<string> {
  const origins = new Set<string>();

  // Always trust our own origin
  if (config.baseOrigin) origins.add(config.baseOrigin);

  // Trust the known client service origins (covers localhost dev where the
  // service URLs aren't under trustedDomain).
  for (const serviceUrl of [config.services.app, config.services.dashboard]) {
    if (!serviceUrl) continue;
    try {
      origins.add(new URL(serviceUrl).origin);
    } catch {
      // invalid URL, skip
    }
  }

  for (const raw of config.trustedOrigins) {
    const trimmed = raw.trim();
    if (!trimmed) continue;
    try {
      origins.add(new URL(trimmed).origin);
    } catch {
      // invalid URL, skip
    }
  }

  return origins;
}

/**
 * Check if a hostname belongs to the trusted domain.
 * e.g. trustedDomain="abacus.dev" matches "app.abacus.dev", "tasks.abacus.dev"
 * but NOT "evil-abacus.dev" or "abacus.dev.evil.com"
 */
function isHostnameTrusted(hostname: string, config: RuntimeConfig): boolean {
  const trustedDomain = config.trustedDomain;
  if (!trustedDomain) return false;

  // Exact match or subdomain match (hostname ends with ".trustedDomain")
  return hostname === trustedDomain || hostname.endsWith(`.${trustedDomain}`);
}

/**
 * Validate whether a redirect URI is safe (same-origin, trusted origin, or trusted domain).
 * Only local relative paths are allowed; authority-like paths are rejected.
 */
export function isRedirectSafe(uri: string, config: RuntimeConfig): boolean {
  // Reject browser-normalized authority forms before treating a path as local.
  // oxlint-disable-next-line no-control-regex -- URL parsers can discard these characters.
  if (/[\\\u0000-\u001f\u007f]/.test(uri) || uri !== uri.trim()) return false;
  if (uri.startsWith("/")) {
    return !/^\/(?:\/|%2f|%5c)/i.test(uri);
  }

  try {
    const parsed = new URL(uri);
    // Block non-http(s) schemes (javascript:, data:, etc.)
    if (
      (parsed.protocol !== "https:" && parsed.protocol !== "http:") ||
      parsed.username ||
      parsed.password
    ) {
      return false;
    }
    // Check exact origin match first, then domain-level trust
    return (
      getTrustedOrigins(config).has(parsed.origin) || isHostnameTrusted(parsed.hostname, config)
    );
  } catch {
    return false;
  }
}

/**
 * Return the URI if safe, otherwise the fallback (default redirect or "/").
 */
export function safeRedirect(uri: string | undefined, config: RuntimeConfig): string {
  return normalizeRedirect(uri, config) ?? (config.services.app || "/");
}

export function safePostAuthRedirect(
  uri: string | undefined,
  config: RuntimeConfig,
): string | null {
  if (!uri || !isRedirectSafe(uri, config) || isAuthFlowRedirect(uri, config)) {
    return null;
  }

  return normalizeRedirect(uri, config);
}

/** Remove only known attribution keys, never functional query values or fragments. */
function normalizeRedirect(uri: string | undefined, config: RuntimeConfig): string | null {
  if (!uri || !isRedirectSafe(uri, config)) return null;
  const parsed = uri.startsWith("/") ? new URL(uri, config.baseOrigin) : new URL(uri);
  let changed = false;
  // Snapshot distinct keys: deleting while iterating URLSearchParams skips entries.
  for (const key of new Set(parsed.searchParams.keys())) {
    if (
      /^(?:_gl|_ga(?:_.*)?|_gcl_.*|gclid|dclid|gbraid|wbraid|utm_.*|fbclid|msclkid)$/i.test(key)
    ) {
      parsed.searchParams.delete(key);
      changed = true;
    }
  }
  if (!changed) return uri;
  const normalized = uri.startsWith("/")
    ? `${parsed.pathname}${parsed.search}${parsed.hash}`
    : parsed.toString();
  return isRedirectSafe(normalized, config) ? normalized : null;
}

export function getGatewayInternalRedirect(uri: string, config: RuntimeConfig): string | null {
  if (!isRedirectSafe(uri, config)) return null;
  if (uri.startsWith("/")) return uri;

  try {
    const parsed = new URL(uri);
    if (parsed.origin !== config.baseOrigin) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

/** Destination for explicit "go to app" links — the old client app. */
export function getFallbackRedirectUrl(config: RuntimeConfig): string {
  return normalizeAbsoluteUrl(config.services.app) ?? "/";
}

/** Default landing for a brand-new signup — the new client dashboard. */
export function getSignupDefaultRedirect(config: RuntimeConfig): string | undefined {
  return normalizeAbsoluteUrl(config.services.dashboard) ?? undefined;
}

/** Package confirmation must continue to the explicit decision inbox. */
export function getInvitationInboxRedirectUrl(config: RuntimeConfig): string {
  const dashboardUrl = normalizeAbsoluteUrl(config.services.dashboard);
  return dashboardUrl ? new URL("/invitations", dashboardUrl).toString() : "/";
}

/**
 * Validate a redirect URI for display purposes (show hostname to user).
 * Returns the hostname only if the URI is safe, otherwise null.
 */
export function safeRedirectHostname(uri: string, config: RuntimeConfig): string | null {
  if (!isRedirectSafe(uri, config)) {
    return null;
  }
  try {
    return new URL(uri).hostname;
  } catch {
    return null;
  }
}

function isAuthFlowRedirect(uri: string, config: RuntimeConfig): boolean {
  const path = getSameGatewayPath(uri, config);
  return path ? AUTH_FLOW_PATHS.has(path) : false;
}

function getSameGatewayPath(uri: string, config: RuntimeConfig): string | null {
  if (uri.startsWith("/")) {
    return normalizePath(uri);
  }

  try {
    const parsed = new URL(uri);
    if (parsed.origin !== config.baseOrigin) return null;
    return normalizePath(parsed.pathname);
  } catch {
    return null;
  }
}

function normalizePath(path: string): string {
  const pathname = path.split(/[?#]/)[0] || "/";
  return pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

function normalizeAbsoluteUrl(url: string | null): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.toString();
  } catch {
    return null;
  }
}
